import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './NotificationBell.module.css';
import BellIcon from '../../icons/BellIcon';
import MessagesIcon from '../../icons/MessagesIcon';
import ListIcon from '../../icons/ListIcon';
import { useNotifications } from '../../lib/useNotifications';

// Past this the panel scrolls rather than growing down the page; the count on
// the badge still says how many there really are.
const VISIBLE = 8;

const KIND_ICON = { support: MessagesIcon, listing: ListIcon };

function timeAgo(at) {
    if (!at) return '';
    const minutes = Math.round((Date.now() - Date.parse(at)) / 60000);
    if (!Number.isFinite(minutes)) return '';
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.round(hours / 24)}d ago`;
}

const NotificationBell = () => {
    const items = useNotifications();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    // Clicking anywhere else closes it — including another header control, so
    // the panel never sits over the page the admin just navigated to.
    useEffect(() => {
        if (!open) return undefined;
        const onDocumentClick = (event) => {
            if (!wrapRef.current?.contains(event.target)) setOpen(false);
        };
        const onKey = (event) => {
            if (event.key === 'Escape') setOpen(false);
        };
        document.addEventListener('click', onDocumentClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('click', onDocumentClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const count = items.length;
    const label = count ? `Notifications, ${count} waiting` : 'Notifications';

    const openItem = (item) => {
        setOpen(false);
        navigate(item.to);
    };

    return (
        <div className={styles.wrap} ref={wrapRef}>
            <button
                type="button"
                className={styles.bellButton}
                aria-label={label}
                aria-expanded={open}
                onClick={() => setOpen((prev) => !prev)}
            >
                <BellIcon size={20} />
                {count > 0 && (
                    <span className={styles.badge} aria-hidden="true">
                        {count > 9 ? '9+' : count}
                    </span>
                )}
            </button>

            {open && (
                <div className={styles.panel} role="dialog" aria-label="Notifications">
                    <div className={`${styles.panelHead} d-flex align-items-center justify-content-between`}>
                        <span className={styles.panelTitle}>Notifications</span>
                        {count > 0 && <span className={styles.panelCount}>{count} waiting</span>}
                    </div>

                    {count === 0 ? (
                        <p className={styles.empty}>Nothing waiting. You're all caught up.</p>
                    ) : (
                        <div className={styles.list}>
                            {items.slice(0, VISIBLE).map((item) => {
                                const Icon = KIND_ICON[item.kind] || MessagesIcon;
                                return (
                                    <button
                                        key={item.id}
                                        type="button"
                                        className={`${styles.item} d-flex align-items-start gap-3 w-100 text-start`}
                                        onClick={() => openItem(item)}
                                    >
                                        <span className={styles.itemIcon}>
                                            <Icon size={16} />
                                        </span>
                                        <span className="flex-grow-1 overflow-hidden">
                                            <span className={styles.itemTitle}>{item.title}</span>
                                            <span className={styles.itemDetail}>{item.detail}</span>
                                        </span>
                                        <span className={styles.itemTime}>{timeAgo(item.at)}</span>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {count > VISIBLE && (
                        <div className={styles.more}>and {count - VISIBLE} more</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationBell;
